import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";

const Register = () => {
  const [form, setForm] = useState({ name: "", email: "", password: "", companyName: "", tinNumber: "", industry: "other", region: "Greater Accra" });
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ ...styles.page, flexDirection: isMobile ? "column" : "row" }}>
      {/* Left panel */}
      <div style={{
        ...styles.left,
        padding: isMobile ? "24px" : "48px",
        minHeight: isMobile ? "auto" : "100vh",
        flex: isMobile ? "none" : 1,
      }}>
        <div style={styles.brand}>
          <div style={styles.logo}>IA</div>
          <span style={styles.brandName}>InvoiceArk</span>
        </div>
        {!isMobile && (
          <>
            <div style={styles.heroText}>
              <h1 style={styles.heroHeading}>Start archiving.<br />Stay compliant.</h1>
              <p style={styles.heroSub}>Set up your company in under 2 minutes. No credit card required.</p>
            </div>
            <div style={styles.checklist}>
              {["GRA VAT invoice tracking", "Full-text invoice search", "Fiscal year reporting", "Team access controls"].map((item) => (
                <div key={item} style={styles.checkItem}>
                  <span style={styles.check}>✓</span>
                  <span style={styles.checkText}>{item}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Right panel — form */}
      <div style={{
        ...styles.right,
        width: isMobile ? "100%" : "520px",
        flex: isMobile ? 1 : "none",
        padding: isMobile ? "24px 20px" : "48px",
        minHeight: isMobile ? "auto" : "100vh",
      }}>
        <div style={{ ...styles.card, maxWidth: isMobile ? "100%" : "420px" }}>
          <h2 style={{ ...styles.cardTitle, fontSize: isMobile ? "22px" : "26px" }}>Create account</h2>
          <p style={styles.cardSub}>Set up your company workspace.</p>
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.sectionLabel}>Your details</div>
            <div style={{ ...styles.row, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
              <div style={styles.field}>
                <label style={styles.label}>Full name</label>
                <input style={styles.input} name="name" placeholder="Kwame Mensah" value={form.name} onChange={handleChange} required />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" name="email" placeholder="you@company.com" value={form.email} onChange={handleChange} required />
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Password</label>
              <input style={styles.input} type="password" name="password" placeholder="Min. 8 characters" value={form.password} onChange={handleChange} required />
            </div>
            <div style={styles.sectionLabel}>Company details</div>
            <div style={styles.field}>
              <label style={styles.label}>Company name</label>
              <input style={styles.input} name="companyName" placeholder="Mensah Trading Ltd." value={form.companyName} onChange={handleChange} required />
            </div>
            <div style={{ ...styles.row, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
              <div style={styles.field}>
                <label style={styles.label}>GRA TIN (optional)</label>
                <input style={styles.input} name="tinNumber" placeholder="C0012345678" value={form.tinNumber} onChange={handleChange} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Industry</label>
                <select style={styles.input} name="industry" value={form.industry} onChange={handleChange}>
                  {["trading","construction","healthcare","hospitality","professional_services","manufacturing","logistics","retail","agriculture","other"].map(i => (
                    <option key={i} value={i}>{i.replace("_", " ")}</option>
                  ))}
                </select>
              </div>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Region</label>
              <select style={styles.input} name="region" value={form.region} onChange={handleChange}>
                {["Greater Accra","Ashanti","Western","Eastern","Central","Northern","Upper East","Upper West","Volta","Brong-Ahafo"].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>
            <button style={loading ? { ...styles.btn, ...styles.btnDisabled } : styles.btn} type="submit" disabled={loading}>
              {loading ? "Creating account..." : "Create account →"}
            </button>
          </form>
          <p style={styles.footer}>Already have an account? <Link to="/login" style={styles.link}>Sign in</Link></p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: { display: "flex", minHeight: "100vh", fontFamily: "'Georgia', serif" },
  left: { background: "#0a0f1e", display: "flex", flexDirection: "column", justifyContent: "space-between", color: "#fff" },
  brand: { display: "flex", alignItems: "center", gap: "12px" },
  logo: { width: "40px", height: "40px", background: "#f6ad55", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "bold", fontSize: "14px", color: "#0a0f1e", fontFamily: "monospace" },
  brandName: { fontSize: "18px", fontWeight: "bold", letterSpacing: "1px" },
  heroText: { flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" },
  heroHeading: { fontSize: "42px", fontWeight: "bold", lineHeight: 1.2, marginBottom: "16px" },
  heroSub: { fontSize: "15px", color: "#a0aec0", lineHeight: 1.6, maxWidth: "300px" },
  checklist: { display: "flex", flexDirection: "column", gap: "12px", paddingTop: "32px", borderTop: "1px solid #1e2a3a" },
  checkItem: { display: "flex", alignItems: "center", gap: "12px" },
  check: { color: "#f6ad55", fontWeight: "bold", fontSize: "16px" },
  checkText: { fontSize: "14px", color: "#a0aec0" },
  right: { background: "#f7f8fc", display: "flex", alignItems: "center", justifyContent: "center", overflowY: "auto" },
  card: { width: "100%" },
  cardTitle: { fontWeight: "bold", color: "#0a0f1e", marginBottom: "6px" },
  cardSub: { fontSize: "14px", color: "#718096", marginBottom: "28px" },
  sectionLabel: { fontSize: "11px", fontWeight: "bold", color: "#f6ad55", textTransform: "uppercase", letterSpacing: "1px", fontFamily: "sans-serif", marginTop: "4px" },
  form: { display: "flex", flexDirection: "column", gap: "16px" },
  row: { display: "grid", gap: "12px" },
  field: { display: "flex", flexDirection: "column", gap: "5px" },
  label: { fontSize: "12px", fontWeight: "600", color: "#2d3748", fontFamily: "sans-serif" },
  input: { padding: "11px 13px", border: "1.5px solid #e2e8f0", borderRadius: "8px", fontSize: "13px", fontFamily: "sans-serif", outline: "none", background: "#fff", color: "#0a0f1e" },
  btn: { padding: "14px", background: "#0a0f1e", color: "#fff", border: "none", borderRadius: "8px", fontSize: "15px", fontWeight: "bold", cursor: "pointer", marginTop: "8px", fontFamily: "sans-serif", letterSpacing: "0.5px" },
  btnDisabled: { background: "#718096", cursor: "not-allowed" },
  footer: { textAlign: "center", fontSize: "13px", color: "#718096", marginTop: "20px", fontFamily: "sans-serif" },
  link: { color: "#f6ad55", fontWeight: "bold", textDecoration: "none" },
};

export default Register;